import { memo } from 'react'
import { motion } from 'framer-motion'

interface LoadingSkeletonProps {
  variant?: 'text' | 'circular' | 'rectangular' | 'card' | 'grid' | 'list'
  width?: string | number
  height?: string | number
  count?: number
  className?: string
  animate?: boolean
}

const LoadingSkeleton = memo(({ 
  variant = 'text',
  width = '100%',
  height,
  count = 1,
  className = '',
  animate = true
}: LoadingSkeletonProps) => {
  const baseClasses = 'bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded'
  const animateClasses = animate ? 'animate-pulse' : ''
  
  const getSkeletonElement = (index: number) => {
    const style = {
      width: typeof width === 'number' ? `${width}px` : width,
      height: typeof height === 'number' ? `${height}px` : height
    }
    
    switch (variant) {
      case 'text':
        return (
          <motion.div
            key={`skeleton-item-${index}`}
            className={`${baseClasses} ${animateClasses} ${className}`}
            style={{
              ...style,
              height: height || '1rem',
              width: index === count - 1 && count > 1 ? '60%' : style.width
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: index * 0.1 }}
          />
        )
        
      case 'circular':
        return (
          <motion.div
            key={`skeleton-item-${index}`}
            className={`${baseClasses} ${animateClasses} rounded-full ${className}`}
            style={{
              width: width || '3rem',
              height: height || '3rem'
            }}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: index * 0.1 }}
          />
        )
        
      case 'rectangular':
        return (
          <motion.div
            key={`skeleton-item-${index}`}
            className={`${baseClasses} ${animateClasses} ${className}`}
            style={{
              ...style,
              height: height || '8rem'
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          />
        )
        
      case 'card':
        return (
          <motion.div
            key={`skeleton-item-${index}`}
            className={`bg-white border border-gray-200 rounded-xl p-6 space-y-4 shadow-sm ${className}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.15 }}
          >
            <div className="flex items-center space-x-4">
              <div className={`${baseClasses} ${animateClasses} rounded-full w-12 h-12`} />
              <div className="space-y-2 flex-1">
                <div className={`${baseClasses} ${animateClasses} h-4 w-3/4`} />
                <div className={`${baseClasses} ${animateClasses} h-3 w-1/2`} />
              </div>
            </div>
            <div className={`${baseClasses} ${animateClasses} h-32 w-full rounded-lg`} />
            <div className="space-y-2">
              <div className={`${baseClasses} ${animateClasses} h-3 w-full`} />
              <div className={`${baseClasses} ${animateClasses} h-3 w-5/6`} />
              <div className={`${baseClasses} ${animateClasses} h-3 w-4/6`} />
            </div>
            <div className="flex justify-between items-center">
              <div className={`${baseClasses} ${animateClasses} h-8 w-20 rounded-lg`} />
              <div className={`${baseClasses} ${animateClasses} h-10 w-24 rounded-lg`} />
            </div>
          </motion.div>
        )
        
      case 'grid':
        return (
          <motion.div
            key={`skeleton-item-${index}`}
            className={`grid grid-cols-10 gap-2 ${className}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: index * 0.1 }}
          >
            {Array.from({ length: 100 }).map((_, gridIndex) => (
              <motion.div
                key={`grid-item-${index}-${gridIndex}`}
                className={`${baseClasses} ${animateClasses} aspect-square rounded-lg`}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ 
                  delay: (index * 0.1) + (gridIndex * 0.01),
                  duration: 0.3 
                }}
              />
            ))}
          </motion.div>
        )
        
      case 'list':
        return (
          <motion.div
            key={`skeleton-item-${index}`}
            className={`bg-white border border-gray-200 rounded-lg p-4 space-y-3 ${className}`}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <div className="flex items-center space-x-3">
              <div className={`${baseClasses} ${animateClasses} rounded-full w-8 h-8`} />
              <div className="space-y-2 flex-1">
                <div className={`${baseClasses} ${animateClasses} h-3 w-3/4`} />
                <div className={`${baseClasses} ${animateClasses} h-2 w-1/2`} />
              </div>
              <div className={`${baseClasses} ${animateClasses} h-6 w-16 rounded`} />
            </div>
          </motion.div>
        )
        
      default:
        return null
    }
  }
  
  if (count === 1) {
    return getSkeletonElement(0)
  }
  
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, index) => 
        getSkeletonElement(index)
      )}
    </div>
  )
})

LoadingSkeleton.displayName = 'LoadingSkeleton'

// Preset components for common use cases
export const RaffleCardSkeleton = memo(() => (
  <LoadingSkeleton variant="card" className="max-w-sm" />
))

export const NumberGridSkeleton = memo(() => (
  <LoadingSkeleton variant="grid" />
))

export const PurchaseListSkeleton = memo(() => (
  <LoadingSkeleton variant="list" count={5} />
))

export const TextSkeleton = memo(({ lines = 3, className }: { lines?: number; className?: string }) => (
  <LoadingSkeleton variant="text" count={lines} className={className} />
))

export const HeaderSkeleton = memo(() => (
  <motion.div
    className="bg-white border-b border-gray-200 p-4"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
  >
    <div className="flex items-center justify-between max-w-7xl mx-auto">
      <LoadingSkeleton variant="rectangular" width={120} height={32} />
      <div className="flex items-center space-x-4">
        <LoadingSkeleton variant="circular" width={40} height={40} />
        <LoadingSkeleton variant="rectangular" width={100} height={36} />
      </div>
    </div>
  </motion.div>
))

export default LoadingSkeleton